import axios from 'axios';

/**
 * Map any thrown error (axios HTTP error, network failure, generic Error) to a
 * short, user-facing message suitable for a toast.
 *
 * The motivating case: while the backend is restarting, requests fail with a
 * 502/503/504 (or no response at all), and raw axios surfaces the useless
 * "Request failed with status code 502". This converts those - and other
 * common failures - into clear guidance, and pulls a human-readable message
 * out of Frappe's error payloads when the server provided one.
 */
export function getFriendlyErrorMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  if (axios.isAxiosError(error)) {
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return 'Request canceled.';
    }

    // No response → server unreachable: down, restarting, DNS/CORS, or offline.
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
        return 'The request timed out. Please try again.';
      }
      return "Can't reach the server. It may be restarting - please wait a moment and try again.";
    }

    const status = error.response.status;
    const serverMsg = extractServerMessage(error.response.data);

    switch (status) {
      case 502:
      case 503:
      case 504:
        return 'The server is restarting or temporarily unavailable. Please wait a moment and try again.';
      case 500:
        return 'The server hit an unexpected error. Please try again - if it keeps happening, contact support.';
      case 408:
        return 'The request timed out. Please try again.';
      case 429:
        return 'Too many requests. Please slow down and try again.';
      case 401:
        return 'Your session has expired. Please sign in again.';
      case 403:
        return serverMsg || "You don't have permission to do this.";
      case 404:
        return serverMsg || 'The requested item could not be found.';
      default:
        return serverMsg || fallback;
    }
  }

  if (error instanceof Error && error.message && !isRawAxiosMessage(error.message)) {
    return error.message;
  }
  return fallback;
}

/** Raw axios/browser strings that carry no useful info for an end user. */
function isRawAxiosMessage(msg: string): boolean {
  return /^request failed with status code/i.test(msg) || /^network error$/i.test(msg);
}

/**
 * Pull a human-readable message out of a Frappe error payload, ignoring
 * tracebacks and HTML error pages (e.g. nginx's "502 Bad Gateway" body).
 * Returns '' when nothing user-friendly is available.
 */
function extractServerMessage(data: unknown): string {
  if (!data) return '';

  if (typeof data === 'string') {
    if (/<html/i.test(data) || isRawAxiosMessage(data)) return '';
    return data.slice(0, 300);
  }

  if (typeof data !== 'object') return '';
  const d = data as Record<string, any>;

  const candidates: unknown[] = [
    // Our custom RPC error envelope: { message: { success: false, error } }.
    d.message?.error,
    // Same envelope returned un-nested (defensive).
    typeof d.error === 'string' ? d.error : undefined,
    d.message?.message,
    typeof d.message === 'string' ? d.message : undefined,
    d._error_message,
  ];

  // `_server_messages` is a JSON-encoded array of JSON-encoded {message,...}.
  if (typeof d._server_messages === 'string') {
    try {
      const arr = JSON.parse(d._server_messages);
      if (Array.isArray(arr) && arr.length) {
        const first = JSON.parse(arr[0]);
        candidates.unshift(first?.message);
      }
    } catch {
      /* not parseable - ignore */
    }
  }

  const found = candidates.find((c) => typeof c === 'string' && c.trim());
  if (!found) return '';
  // Strip any HTML tags Frappe embeds (e.g. <b>…</b>) and clamp the length.
  return String(found).replace(/<[^>]+>/g, '').trim().slice(0, 300);
}
